import { DEFAULT_ITEMS_PER_PAGE } from '@/constants';
import { TCustomAxiosResponse } from '@/services/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface IUseInfinitePaginationParams<T extends unknown[]> {
  query: (page: number, limit?: number) => Promise<TCustomAxiosResponse<T>>;
  queryKey: string[];
  enabled?: boolean;
  limit?: number;
  getId?: (item: T[number]) => string;
}

/**
 *
 * @param query - the query to be executed. Must receive `page: number` param and return TCustomAxiosResponse<T>
 * @returns isLoading: boolean, data: T[], fetchNextPage: () => void, isFetchingNextPage: boolean, hasNextPage: boolean;
 */
export const useInfinitePagination = <T extends unknown[]>({
  query,
  queryKey,
  enabled,
  limit = DEFAULT_ITEMS_PER_PAGE,
  getId,
}: IUseInfinitePaginationParams<T>) => {
  const { isLoading, data, refetch, isRefetching, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery<
    TCustomAxiosResponse<T>
  >({
    // `limit` is part of the identity of this list, not just an argument: getNextPageParam
    // below compares each page's length against it, so two mounts that share a key but
    // request different page sizes would page over one another's cache — duplicating or
    // skipping rows. Keep it in the key.
    //
    // Appended rather than nested so that `[queryKeys.x]` still prefix-matches this query
    // for invalidation; wrapping the caller's key in an array would silently break that.
    queryKey: [...queryKey, limit],
    enabled,
    queryFn: async ({ pageParam: page }) => {
      const response = await query(page as number, limit);
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, __, lastPageParam) => {
      if (lastPage.data.length < limit) {
        return undefined;
      }
      return (lastPageParam as number) + 1;
    },
  });

  const flattenData = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.data);
    if (!items || !getId) return items as T;
    const seen = new Set<string>();
    return items.filter((item) => {
      const id = getId(item);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }) as T;
  }, [data?.pages, getId]);

  return { isLoading, data: flattenData, refetch, isRefetching, fetchNextPage, isFetchingNextPage, hasNextPage };
};
